import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import { FC, Fragment } from 'react'
import { formatMoney } from 'accounting-js'
import {
  calculate80mmPageHeight,
  getState,
  STATES,
  styleSheet as styles,
  styles80mm
} from './PrintProductList.constants'
import { useTranslation } from 'react-i18next'
import { formatHourWithUtc } from '../../../../../utils/dates'
import { standardDate } from '../../../flag-management.tools'
import { Job } from '../../../../../refactory/modules/jobs/domain/job'

interface PrintProductList80mmProps {
  job: Job
  itemsCount: number
  paymentValue: string
  packages?: any[]
  clientLogoURL?: string
  symbolValue: {
    symbol: string
    format: string
    precision: any
  }
}

export const PrintProductList80mm: FC<PrintProductList80mmProps> = ({
  job,
  clientLogoURL,
  itemsCount,
  paymentValue,
  packages,
  symbolValue
}) => {
  const {
    jobNumber,
    origin: { name: originName },
    storeTzCreatedAt,
    receiver: { firstName: clientFirstName, lastName: clientLastName, phoneNumber, email },
    destination: { address, addressTwo },
    paymentMethod,
    storeTzSlotFrom,
    storeTzSlotTo
  } = job
  const { t } = useTranslation()
  return (
    <Document title={jobNumber}>
      <Page
        size={{
          width: 226.77,
          height: calculate80mmPageHeight(packages)
        }}
        style={styles.page}
        wrap
      >
        {/* Logo */}
        <View style={styles.row}>
          <View style={styles.brand}>
            {!!clientLogoURL && <Image src={clientLogoURL} style={styles.logoImage} />}
            <Image src='/powered-by.png' style={styles.powered} />
          </View>
        </View>

        {/* Job Details and client info */}
        <View style={{ ...styles80mm.row, ...styles80mm.marginTop15, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.details')}</Text>
          <Text style={styles80mm.text}>{jobNumber}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.origin')}</Text>
          <Text style={styles80mm.text}>{originName}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.createdAt')}</Text>
          <Text style={styles80mm.text}>{`${moment(formatHourWithUtc(standardDate(storeTzCreatedAt))).format(
            'DD/MM/YYYY LT'
          )}`}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.client')}</Text>
          <Text style={styles80mm.text}>{`${clientFirstName} ${clientLastName}`}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.email')}</Text>
          <Text style={styles80mm.text}>{email}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.phone')}</Text>
          <Text style={styles80mm.text}>{phoneNumber}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.item-list')}</Text>
          <Text style={styles80mm.text}>{`${itemsCount} ${t('jobs.job.info.items-view.print.printed')}`}</Text>
        </View>
        <View style={{ ...styles80mm.row }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.total-items')}</Text>
          <Text style={styles80mm.text}>{`${job.items.length} ${t(
            'jobs.job.info.items-view.print.items-label'
          )}`}</Text>
        </View>

        {/* Destination */}
        <View style={{ ...styles80mm.row, ...styles80mm.marginTop15 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.destination')}</Text>
          <Text style={styles80mm.text}>{address}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.destination-detail')}</Text>
          <Text style={styles80mm.text}>{addressTwo}</Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.delivery')}</Text>
          <Text style={styles80mm.text}>
            {`${moment(formatHourWithUtc(standardDate(storeTzSlotFrom))).format('DD/MM/YYYY LT')} - ${moment(
              formatHourWithUtc(standardDate(storeTzSlotTo))
            ).format('LT')}`}
          </Text>
        </View>
        <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.payment-method')}</Text>
          <Text style={styles80mm.text}>{t(`${paymentMethod}`)}</Text>
        </View>
        <View style={{ ...styles80mm.row }}>
          <Text style={styles80mm.label}>{t('jobs.job.info.items-view.print.labels.payment-value')}</Text>
          <Text style={styles80mm.text}>{paymentValue}</Text>
        </View>

        {/* Items */}
        <View style={{ ...styles80mm.fullWidth, ...styles80mm.marginTop15, ...styles80mm.col }}>
          {packages &&
            packages.map(({ categories, package: _package }, packageIdx) => {
              return (
                <Fragment key={`package-${packageIdx}`}>
                  {_package && Object.keys(categories).length > 0 ? (
                    <View style={{ backgroundColor: '#F9F9F9', marginTop: 8 }}>
                      <Text style={styles.package}>{t('jobs.job.info.items-view.print.labels.package', _package)}</Text>
                    </View>
                  ) : null}

                  {Object.keys(categories).map((category, idx) => (
                    <Fragment key={`category-${idx}`}>
                      <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
                        <Text
                          style={{ ...styles80mm.text, ...styles80mm.capitalize }}
                        >{`${category} (${categories[category].length})`}</Text>
                      </View>

                      {categories[category] &&
                        categories[category].map((item, itemIndex) => {
                          const {
                            name,
                            reference,
                            attributes: { ean },
                            state,
                            presentation,
                            presentationV2,
                            foundQuantity,
                            rejected: { rejectedByDriver, rejectedByUser }
                          } = item

                          const unitPrice = `${formatMoney(
                            presentationV2 ? presentationV2.price : presentation.price.amount,
                            symbolValue
                          )}`

                          const quantity = state === STATES.REJECTED ? rejectedByDriver + rejectedByUser : foundQuantity

                          const itemQuantity = ` ${quantity} ${t('jobs.job.info.items-view.product-card.from')} ${
                            presentationV2
                              ? `${presentationV2.quantity} ${presentationV2.unit}`
                              : `${presentation.quantity} ${presentation.unit}`
                          } `

                          return (
                            <Fragment key={`item-${itemIndex}`}>
                              <View style={{ ...styles80mm.fullWidth, ...styles80mm.row }}>
                                <Text
                                  style={{
                                    ...styles80mm.capitalize,
                                    ...styles80mm.paddingLeft,
                                    ...styles80mm.text,
                                    flex: 2
                                  }}
                                >
                                  {name}
                                </Text>
                                <Text style={{ ...styles80mm.label, ...styles80mm.paddingLeft, flex: 1 }}>
                                  {unitPrice}
                                </Text>
                              </View>
                              <View style={{ ...styles80mm.row }}>
                                <Text style={{ ...styles.text, ...styles80mm.paddingLeft }}>
                                  {t(`jobs.job.info.items-view.print.states.${getState(state).toLocaleLowerCase()}`)}
                                </Text>
                              </View>
                              <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
                                <Text style={{ ...styles.text, ...styles80mm.paddingLeft }}>{itemQuantity}</Text>
                              </View>

                              <View style={{ ...styles80mm.row }}>
                                <Text style={{ ...styles.text, ...styles80mm.paddingLeft }}>
                                  {t('jobs.job.info.items-view.print.labels.reference')}: {reference}
                                </Text>
                              </View>
                              <View style={{ ...styles80mm.row, ...styles80mm.marginBottom7 }}>
                                <Text style={{ ...styles.text, ...styles80mm.paddingLeft }}>
                                  {t('jobs.job.info.items-view.print.labels.ean')}:{' '}
                                  {ean?.replace(/[\[\]"]/g, '')?.replace(/,/g, ', ')}
                                </Text>
                              </View>
                            </Fragment>
                          )
                        })}
                    </Fragment>
                  ))}
                </Fragment>
              )
            })}
        </View>

        <View style={{ ...styles80mm.row, ...styles80mm.marginTop7 }}>
          <Text style={{ ...styles80mm.text }}>{`${t('jobs.job.info.items-view.print.generated-at')}: `}</Text>
          <Text style={{ ...styles80mm.label }}>{moment().format('DD/MM/YYYY · LT')}</Text>
        </View>
      </Page>
    </Document>
  )
}
